/**
 * Wall-clock <-> instant conversion for an arbitrary IANA zone.
 *
 * `Date` only ever holds an instant (epoch ms); it has no notion of "this
 * clock reading in that zone". Everything here is built on
 * `Intl.DateTimeFormat.formatToParts`, which is the one part of the platform
 * that actually knows a zone's offset (DST included) for a given instant.
 */

/** The IANA zone's UTC offset, in milliseconds, at `instantMs`. Positive
 *  east of Greenwich (Asia/Kolkata reads +19800000, five and a half hours). */
function offsetMsAt(timeZone: string, instantMs: number): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(new Date(instantMs))

  const get = (type: string) => Number(parts.find((p) => p.type === type)?.value)

  // Read the zone's wall clock at this instant and rebuild it as if those
  // same numbers were UTC. The gap between that and the instant we asked
  // about is exactly the zone's offset.
  const asUtc = Date.UTC(
    get('year'),
    get('month') - 1,
    get('day'),
    get('hour'),
    get('minute'),
    get('second'),
  )
  return asUtc - instantMs
}

/**
 * The instant (epoch ms) at which the wall clock in `timeZone` reads the
 * given date and time.
 *
 * `guess` treats the picked date/time as if it were already UTC, then
 * corrects by the zone's offset at that guess. Offsets can change between
 * the guess and the corrected instant across a DST boundary, so the offset
 * is recomputed once at the corrected instant and, if it moved, applied a
 * second time.
 *
 * A wall time that a spring-forward gap skips entirely (e.g. 2:30 AM on the
 * day America/New_York jumps from 2:00 to 3:00) has no true answer; this
 * returns whichever side the two-pass correction lands on, which is
 * consistent but not meaningfully "more correct" than the other side.
 */
export function zonedTimeToUtcMs(
  year: number,
  month1to12: number,
  day: number,
  hour: number,
  minute: number,
  timeZone: string,
): number {
  const guess = Date.UTC(year, month1to12 - 1, day, hour, minute, 0, 0)
  const offset1 = offsetMsAt(timeZone, guess)
  const result1 = guess - offset1
  const offset2 = offsetMsAt(timeZone, result1)
  return offset2 === offset1 ? result1 : guess - offset2
}

export interface ZonedParts {
  year: number
  month: number // 1-12
  day: number
  hour: number
  minute: number
}

/** The wall-clock date and time `timeZone` reads at instant `ms`. */
export function utcMsToZonedParts(ms: number, timeZone: string): ZonedParts {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).formatToParts(new Date(ms))

  const get = (type: string) => Number(parts.find((p) => p.type === type)?.value)

  return {
    year: get('year'),
    month: get('month'),
    day: get('day'),
    hour: get('hour'),
    minute: get('minute'),
  }
}
